<template>
  <div class="advanced-select__container">
    <multiselect
      v-model="selectedValue"
      :label="label"
      :name="name"
      :disabled="disabled"
      :placeholder="placeholder"
      :options="options"
      :searchable="true"
      :show-labels="false"
      :close-on-select="closeSelected"
      :track-by="label"
      :multiple="multiple"
      :open-direction="direction"
      :max-height="250"
      :hide-selected="hide"
      :tabindex="tabindex"
      :taggable="taggable"
      @open="onOpen"
      @close="onClose"
      @search-change="onSearch"
      @input="onChange"
      @keydown.enter.native.stop="enter"
    />
    <md-input type="hidden" />
  </div>
</template>

<script>
import Multiselect from 'vue-multiselect'
import getClosestVueParent from '../../utils/getClosestVueParent'

export default {
  name: 'AdvancedSelect',
  components: {
    'multiselect': Multiselect
  },
  props: {
    options: [Object, Array],
    placeholder: {
      type: String,
      default: ''
    },
    searchable: {
      type: Boolean,
      default: true

    },
    taggable: {
      type: Boolean,
      default: true

    },
    label: {
      type: String,
      default: 'label'
    },
    disabled: {
      type: Boolean,
      default: false
    },
    name: String,
    closeSelected: true,
    multiple: false,
    value: null,
    hide: false,
    direction: {
      type: String,
      default: 'bottom'
    }
  },
  computed: {
    parentContainer () {
      return getClosestVueParent(this.$parent, 'md-input-container')
    },
    selectedValue: {
      get () {
        return this.value
      },
      set (val) {
        this.$emit('input', val)
      }
    },
    tabindex: function () {
      return this.disabled ? -1 : 0
    }
  },
  methods: {
    onSelect () {
      this.parentContainer.$el.classList.add('md-has-value')
    },
    onOpen () {
      this.parentContainer.$el.classList.add('md-input-focused')
    },
    onClose () {
      this.parentContainer.$el.classList.remove('md-input-focused')
    },
    onChange () {
      this.$emit('onChange')
    },
    onSearch () {
      if (this.parentContainer.input.value) {
        this.parentContainer.$el.classList.add('md-has-value')
      } else {
        this.parentContainer.$el.classList.remove('md-has-value')
      }
    },
    hasChanged () {
      this.parentContainer.$el.classList.add('md-has-value')
    },
    enter () {
      this.$emit('enter')
    }
  }
}
</script>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>
<style lang="scss">
@import '../../../static/stylesheets/variables';

  .md-input-container {
    &.md-input-placeholder {
      &.md-advanced-select {
        label {
          opacity: 1;
          top: 22px;
          font-size: 16px;
          color: #222222;
        }

        &.md-input-focused {
          label {
            opacity: 1;
            font-size: 16px;
            color: #222222 !important;
          }
        }

        &.md-has-value {
          label {
            top: 0;
            font-size: 12px;
            color: #858585 !important;
          }
        }

        &.md-input-invalid {
          label {
            color: #222222 !important;
          }

          &.md-has-value {
            label {
              color: #858585 !important;
            }
          }
        }
      }
    }
  }

  /* COMPONENT: commom/AdvancedSelect.vue */
  .advanced-select__container {
    position: absolute;
    width: 100%;
    cursor: pointer;

    .multiselect {
      min-height: 30px;

      &--active .multiselect__select {
        transform: scaleY(-1);
      }

      &__select {
        text-align: right;
        padding: 0;
        height: 30px;
        width: 100%;
        top: 0;
        right: 0;
        @media screen and (-ms-high-contrast: active), (-ms-high-contrast: none) {
          z-index: 0;
        }

        &:before {
          position: absolute;
          color: #c2c2c2;
          border-color: #c2c2c2 transparent transparent;
          top: 35%;
        }
      }

      &__tags {
        border-radius: 0;
        border: 0;
        padding: 5px 0px 0 0;
        background: none;
        z-index: 1;
        overflow-x: auto;
        overflow-y: hidden;
        white-space: nowrap;
        width: 90%;
        font-size: 11px;
        font-weight: 400;
      }

      &__tag {
        background-color: $brand-primary;
        margin-right: 5px;

        &-icon {
          line-height: 1.82em;

          @media screen and (-ms-high-contrast: active), (-ms-high-contrast: none) {
            line-height: 1.7em;
          }
        }

        &-icon:hover{
          background-color: $brand-primary-highlight;
        }

        &-icon:after {
          color: white;
        }
      }

      &__single {
        padding: 1px 0 0;
        background: none;
        white-space: nowrap;
        overflow: hidden;
        width: auto;
        text-overflow: ellipsis;
      }

      &__content-wrapper {
        min-height: 56px;
        border-radius: 0;
        box-shadow: 0 1px 5px rgba(0,0,0,.2), 0 2px 2px rgba(0,0,0,.14), 0 3px 1px -2px rgba(0,0,0,.12);
        border: 0;
        margin-top: 0px;
        width: auto;
        min-width: 100%;
        z-index: 3;
        overflow-x: hidden;
      }

      &__content {
        padding: 8px 0;
        z-index: 4;
      }

      &__element {
        & + li {
          margin-top: 0;
        }
      }

      &__option {
        font-size: 14px;
        font-weight: 400;
        color: #303030;
        min-height: 48px;
        display: flex;
        align-items: center;


        &:hover,
        &:active,
        &:focus,
        &--highlight {
          background: #dddddd;
        }

        &--selected,
        &--selected.multiselect__option--highlight {
          background-color: $brand-primary;
          color: white;
        }
        &--selected:hover,
        &--selected:focus {
          background-color: $brand-primary-highlight;
          color: white;
        }

        &--disabled,
        &--disabled:hover,
        &--disabled:focus,
        &--disabled.multiselect__option--highlight {
          background: transparent !important;
          color: #858585;
        }
      }
    }
  }
</style>
